import React, { useState, useEffect } from "react";
import { supabase } from '../../supabase';
import { authHeaders } from '../../utils/api';
import { CATEGORIES } from '../../constants/categories';
import { fmtDate } from '../../utils/formatting';
import { getCityFromZip } from '../../constants/zipData';

function VendorApplyModal({ opp, allOpps, onClose }) {
  const seriesDates = (allOpps||[]).filter(o=>o.eventName===opp.eventName&&o.source==='Recurring Series'&&o.id!==opp.id).sort((a,b)=>(a.date||'').localeCompare(b.date||''));
  const isSeries = seriesDates.length > 0;
  const [selectedDates, setSelectedDates] = useState([opp.id]);
  const toggleDate = (id) => setSelectedDates(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const [form, setForm] = useState({ vendorName:'', contactName:'', email:'', phone:'', category:'', message:'', vendorId:null });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [vendorProfiles, setVendorProfiles] = useState([]);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const fillFrom = (data) => {
    if (!data) return;
    setForm(f => ({
      ...f,
      vendorName: data.name || '',
      contactName: data.contact_name || '',
      email: data.contact_email || '',
      phone: data.contact_phone || '',
      category: data.category || '',
      vendorId: data.id || null,
    }));
    setAutoFilled(true);
  };

  // Load all vendor profiles for this user
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const profiles = [];
        const { data: byId } = await supabase.from('vendors').select('id,name,contact_name,contact_email,contact_phone,category').eq('user_id', session.user.id);
        if (byId) profiles.push(...byId);
        const { data: byEmail } = await supabase.from('vendors').select('id,name,contact_name,contact_email,contact_phone,category').ilike('contact_email', session.user.email);
        if (byEmail) byEmail.forEach(v => { if (!profiles.some(p=>p.id===v.id)) profiles.push(v); });
        setVendorProfiles(profiles);
        if (profiles.length > 0) fillFrom(profiles[0]);
      } else {
        const vid = localStorage.getItem('sjvm_calendar_vendor_id');
        if (vid) {
          const { data } = await supabase.from('vendors').select('id,name,contact_name,contact_email,contact_phone,category').eq('id', vid).limit(1);
          if (data?.[0]) { setVendorProfiles([data[0]]); fillFrom(data[0]); }
        }
      }
    })();
  }, []);

  const handleSubmit = async () => {
    if (!form.vendorName || !form.email || !form.contactName) { alert('Please fill in your business name, contact name, and email.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(form.email)) { alert('Please enter a valid email.'); return; }
    setSubmitting(true);
    // Build list of events to apply to (current + selected recurring dates)
    const eventsToApply = [opp];
    if (isSeries) {
      seriesDates.forEach(s => { if (selectedDates.includes(s.id)) eventsToApply.push(s); });
    }
    let anyError = false;
    let firstToken = null;
    for (const evt of eventsToApply) {
      // Check if already applied
      if (form.vendorId) {
        const { data: existing } = await supabase.from('booking_requests').select('id').eq('vendor_id', form.vendorId).eq('event_name', evt.eventName).eq('event_date', evt.date).limit(1);
        if (existing?.[0]) continue; // skip already applied dates
      }
      const responseToken = crypto.randomUUID();
      if (!firstToken) firstToken = responseToken;
      const payload = {
        id: Date.now() + Math.floor(Math.random() * 10000),
        session_id: 'vendor-application',
        vendor_id: form.vendorId || null, vendor_name: form.vendorName,
        vendor_emoji: '', vendor_category: form.category || '',
        host_name: evt.contactName || opp.contactName, host_email: evt.contactEmail || opp.contactEmail,
        event_name: evt.eventName, event_type: evt.eventType,
        event_zip: evt.zip, event_date: evt.date,
        start_time: evt.startTime || null, end_time: evt.endTime || null,
        address: '', attendance: '', vendor_count: String(evt.spots || ''),
        budget: evt.boothFee || '', notes: form.message || null,
        status: 'pending', sent_at: new Date().toISOString(),
        response_token: responseToken,
      };
      let { error } = await supabase.from('booking_requests').insert(payload);
      if (error && (error.code === '42703' || error.message?.includes('column'))) {
        const { response_token: _rt, ...fallback } = payload;
        ({ error } = await supabase.from('booking_requests').insert(fallback));
      }
      if (error) { console.error('[VENDOR APPLY] FAILED for', evt.date, ':', error.message, error.code, error.details, error.hint); anyError = true; }
    }
    const error = anyError;
    if (error) {
      console.error('Application error:', error);
      alert('Failed to submit application. Please try again.');
      setSubmitting(false);
      return;
    }
    // Email the host: "A vendor has applied to your event"
    if (opp.contactEmail) {
      authHeaders().then(h => fetch('/api/send-message-notification', {
        method: 'POST', headers: h,
        body: JSON.stringify({
          recipientEmail: opp.contactEmail,
          recipientName: opp.contactName,
          senderName: form.vendorName,
          senderType: 'vendor',
          recipientType: 'host',
          eventName: opp.eventName,
          messagePreview: `${form.vendorName} (${form.category || 'Vendor'}) has applied to ${opp.eventName} on ${opp.date}. Log in to your Host Dashboard to review, accept, or decline.`,
        }),
      })).catch(err => console.error('Failed to send host notification:', err));
    }
    // Email the vendor: confirm application was submitted
    if (form.email) {
      authHeaders().then(h => fetch('/api/send-message-notification', {
        method: 'POST', headers: h,
        body: JSON.stringify({
          recipientEmail: form.email,
          recipientName: form.contactName || form.vendorName,
          senderName: opp.contactName || 'Event Host',
          senderType: 'system',
          recipientType: 'vendor',
          eventName: opp.eventName,
          messagePreview: `Your application to ${opp.eventName} on ${opp.date} has been submitted! The host will review and respond. You'll receive an email when they accept or decline.`,
        }),
      })).catch(err => console.error('Failed to send vendor confirmation:', err));
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,maxWidth:520,width:'100%',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{background:'#1a1410',padding:'20px 28px',borderRadius:'16px 16px 0 0'}}>
          <div style={{fontSize:14,color:'#a89a8a'}}>Apply to Vend</div>
          <div style={{fontSize:20,fontWeight:700,color:'#e8c97a',fontFamily:'Playfair Display,serif'}}>{opp.eventName}</div>
          <div style={{fontSize:12,color:'#a89a8a',marginTop:4}}>{fmtDate(opp.date)} {'\u00b7'} {opp.eventType} {'\u00b7'} {getCityFromZip(opp.zip) || opp.zip}{(opp.orgName||opp.contactName) ? ` · Hosted by ${opp.orgName||opp.contactName}` : ''}</div>
        </div>
        <div style={{padding:'24px 28px'}}>
          {submitted ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:32,marginBottom:12}}>{'\u2705'}</div>
              <div style={{fontSize:18,fontWeight:700,color:'#1a6b3a',marginBottom:8}}>Application Sent!</div>
              <div style={{fontSize:14,color:'#7a6a5a',marginBottom:16}}>The host has been notified and will review your application. They'll reach out if they'd like to confirm your spot.</div>
              <button onClick={onClose} style={{background:'#1a1410',color:'#e8c97a',border:'none',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Close</button>
            </div>
          ) : (
            <>
              {autoFilled ? (
                <>
                  {vendorProfiles.length > 1 && (
                    <div style={{marginBottom:12}}>
                      <div style={{fontSize:12,fontWeight:600,color:'#7a6a5a',marginBottom:6}}>Apply as:</div>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        {vendorProfiles.map(p=>(
                          <button key={p.id} onClick={()=>fillFrom(p)} style={{padding:'6px 14px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:form.vendorId===p.id?'2px solid #c8a850':'1px solid #e8ddd0',background:form.vendorId===p.id?'#fdf9f0':'#fff',color:'#1a1410'}}>{p.name}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{background:'#d4f4e0',border:'1px solid #b8e8c8',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:13,color:'#1a6b3a'}}>
                    <strong>Applying as:</strong> {form.vendorName} ({form.email}){form.category ? ` \u00b7 ${form.category}` : ''}
                  </div>
                  {isSeries && (
                    <div style={{background:'#e8f4fd',border:'1px solid #b8d8f0',borderRadius:8,padding:'12px 14px',marginBottom:12}}>
                      <div style={{fontSize:13,fontWeight:700,color:'#1a4a6b',marginBottom:6}}>{'\ud83d\udd04'} This is a recurring event series</div>
                      <div style={{fontSize:12,color:'#1a4a6b',marginBottom:8}}>Select the dates you'd like to apply for:</div>
                      <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',padding:'4px 0',fontWeight:600,color:'#1a1410',textTransform:'none',letterSpacing:0}}>
                          <input type="checkbox" checked={true} disabled style={{width:16,height:16}} />
                          {fmtDate(opp.date)} (this event)
                        </label>
                        {seriesDates.map(s=>(
                          <label key={s.id} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',padding:'4px 0',textTransform:'none',letterSpacing:0,fontWeight:400,color:'#1a1410'}}>
                            <input type="checkbox" checked={selectedDates.includes(s.id)} onChange={()=>toggleDate(s.id)} style={{width:16,height:16}} />
                            {fmtDate(s.date)}
                          </label>
                        ))}
                      </div>
                      <div style={{display:'flex',gap:8,marginTop:8}}>
                        <button type="button" onClick={()=>setSelectedDates([opp.id,...seriesDates.map(s=>s.id)])} style={{background:'#1a4a6b',color:'#fff',border:'none',borderRadius:4,padding:'4px 10px',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Select All</button>
                        <button type="button" onClick={()=>setSelectedDates([opp.id])} style={{background:'#f5f0ea',color:'#7a6a5a',border:'1px solid #e0d5c5',borderRadius:4,padding:'4px 10px',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>This Date Only</button>
                      </div>
                    </div>
                  )}
                  <div className="form-group" style={{marginBottom:16}}>
                    <label>Message to Host (optional)</label>
                    <textarea value={form.message} onChange={e=>set('message',e.target.value)} rows={3}
                      placeholder="Tell the host about your products, experience, or any questions..." />
                  </div>
                  <div style={{display:'flex',gap:10}}>
                    <button onClick={handleSubmit} disabled={submitting}
                      style={{flex:2,background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 0',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',opacity:submitting?0.5:1}}>
                      {submitting ? 'Submitting...' : 'Apply to Vend'}
                    </button>
                    <button onClick={onClose}
                      style={{flex:1,background:'#f5f0ea',color:'#1a1410',border:'1px solid #e0d5c5',borderRadius:8,padding:'12px 0',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-2col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                    <div className="form-group"><label>Business Name *</label><input value={form.vendorName} onChange={e=>set('vendorName',e.target.value)} placeholder="Your business name" /></div>
                    <div className="form-group"><label>Contact Name *</label><input value={form.contactName} onChange={e=>set('contactName',e.target.value)} placeholder="Your name" /></div>
                  </div>
                  <div className="modal-2col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                    <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="you@email.com" /></div>
                    <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="(555) 555-5555" /></div>
                  </div>
                  <div className="form-group" style={{marginBottom:12}}>
                    <label>Your Category</label>
                    <select value={form.category} onChange={e=>set('category',e.target.value)} style={{width:'100%',padding:'8px 10px',border:'1px solid #e0d5c5',borderRadius:6,fontSize:14,fontFamily:'DM Sans,sans-serif'}}>
                      <option value="">Select your category</option>
                      {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{marginBottom:16}}>
                    <label>Message to Host (optional)</label>
                    <textarea value={form.message} onChange={e=>set('message',e.target.value)} rows={3}
                      placeholder="Tell the host about your products, experience, or any questions..." />
                  </div>
                  <div style={{display:'flex',gap:10}}>
                    <button onClick={handleSubmit} disabled={submitting}
                      style={{flex:2,background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 0',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',opacity:submitting?0.5:1}}>
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </button>
                    <button onClick={onClose}
                      style={{flex:1,background:'#f5f0ea',color:'#1a1410',border:'1px solid #e0d5c5',borderRadius:8,padding:'12px 0',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorApplyModal;
